import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const NetWorthTrendChart = ({ historicalData, currentNetWorth }) => {
  const [chartType, setChartType] = useState('combined'); // 'combined', 'assets', 'liabilities'
  const [timeRange, setTimeRange] = useState('6m'); // '3m', '6m', '1y', 'all'

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const formatShortCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000)?.toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000)?.toFixed(0)}k`;
    }
    return formatCurrency(amount);
  };

  // Filter data based on time range
  const getFilteredData = () => {
    if (!historicalData) return [];
    
    const now = new Date();
    let monthsBack = 6;
    
    switch (timeRange) {
      case '3m': monthsBack = 3; break;
      case '6m': monthsBack = 6; break;
      case '1y': monthsBack = 12; break;
      case 'all': return historicalData;
      default: monthsBack = 6;
    }
    
    return historicalData?.slice(-monthsBack);
  };

  const filteredData = getFilteredData();

  // Calculate trends
  const calculateTrend = () => {
    if (filteredData?.length < 2) return { change: 0, percentage: 0, direction: 'neutral' };
    
    const firstValue = filteredData?.[0]?.netWorth;
    const lastValue = filteredData?.[filteredData?.length - 1]?.netWorth;
    const change = lastValue - firstValue;
    const percentage = ((change / firstValue) * 100)?.toFixed(1);
    const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';
    
    return { change, percentage, direction };
  };

  const trend = calculateTrend();

  // Calculate average monthly growth
  const avgMonthlyGrowth = () => {
    if (filteredData?.length < 2) return 0;
    
    let totalGrowth = 0;
    for (let i = 1; i < filteredData?.length; i++) {
      totalGrowth += filteredData?.[i]?.netWorth - filteredData?.[i-1]?.netWorth;
    }
    return totalGrowth / (filteredData?.length - 1);
  };

  const monthlyGrowth = avgMonthlyGrowth();

  // Find highest and lowest points
  const getExtremes = () => {
    if (!filteredData?.length) return { highest: 0, lowest: 0 };
    
    const values = filteredData?.map(d => d?.netWorth);
    return {
      highest: Math.max(...values),
      lowest: Math.min(...values)
    };
  };

  const extremes = getExtremes();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-elevation-3">
          <p className="text-sm font-semibold text-foreground mb-2">
            {payload?.[0]?.payload?.month}
          </p>
          {chartType === 'combined' && (
            <>
              <div className="flex items-center justify-between gap-4 mb-1">
                <span className="text-xs text-muted-foreground">Net Worth:</span>
                <span className="text-sm font-bold text-primary">
                  {formatCurrency(payload?.[0]?.payload?.netWorth)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 mb-1">
                <span className="text-xs text-success">Assets:</span>
                <span className="text-sm font-semibold text-success">
                  {formatCurrency(payload?.[0]?.payload?.assets)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs text-error">Liabilities:</span>
                <span className="text-sm font-semibold text-error">
                  {formatCurrency(payload?.[0]?.payload?.liabilities)}
                </span>
              </div>
            </>
          )}
          {chartType === 'assets' && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Assets:</span>
              <span className="text-sm font-bold text-success">
                {formatCurrency(payload?.[0]?.value)}
              </span>
            </div>
          )}
          {chartType === 'liabilities' && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Liabilities:</span>
              <span className="text-sm font-bold text-error">
                {formatCurrency(payload?.[0]?.value)}
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-2">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
            Net Worth Trend
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Historical asset and liability changes over time
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {['3m', '6m', '1y', 'all']?.map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-2 md:px-3 py-1 text-xs font-medium rounded transition-smooth ${
                  timeRange === range
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {range?.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="p-3 md:p-4 bg-primary/10 border border-primary/20 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-primary" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Current Net Worth
            </span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-foreground">
            {formatCurrency(currentNetWorth?.netWorth)}
          </p>
        </div>

        <div className={`p-3 md:p-4 rounded-xl ${
          trend?.direction === 'up' ?'bg-success/10 border border-success/20' 
            : trend?.direction === 'down' ?'bg-error/10 border border-error/20' :'bg-muted'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Icon 
              name={trend?.direction === 'up' ? 'ArrowUp' : trend?.direction === 'down' ? 'ArrowDown' : 'Minus'} 
              size={16} 
              className={trend?.direction === 'up' ? 'text-success' : trend?.direction === 'down' ? 'text-error' : 'text-muted-foreground'} 
            />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Period Change
            </span>
          </div>
          <p className={`text-xl md:text-2xl font-bold ${
            trend?.direction === 'up' ? 'text-success' : trend?.direction === 'down' ? 'text-error' : 'text-foreground'
          }`}>
            {trend?.change >= 0 ? '+' : ''}{formatCurrency(trend?.change)}
            <span className="text-sm ml-2">({trend?.percentage}%)</span>
          </p>
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="BarChart3" size={16} className="text-muted-foreground" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Avg Monthly Growth
            </span>
          </div>
          <p className={`text-xl md:text-2xl font-bold ${
            monthlyGrowth >= 0 ? 'text-success' : 'text-error'
          }`}>
            {monthlyGrowth >= 0 ? '+' : ''}{formatCurrency(monthlyGrowth)}
          </p>
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Activity" size={16} className="text-muted-foreground" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Range
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-xs text-muted-foreground">
              High: <span className="font-semibold text-foreground">{formatShortCurrency(extremes?.highest)}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Low: <span className="font-semibold text-foreground">{formatShortCurrency(extremes?.lowest)}</span>
            </p>
          </div>
        </div>
      </div>
      {/* Chart Type Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs md:text-sm font-medium text-muted-foreground">View:</span>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {[
            { value: 'combined', label: 'Combined', icon: 'TrendingUp' },
            { value: 'assets', label: 'Assets', icon: 'ArrowUp' },
            { value: 'liabilities', label: 'Liabilities', icon: 'ArrowDown' }
          ]?.map((type) => (
            <button
              key={type?.value}
              onClick={() => setChartType(type?.value)}
              className={`flex items-center gap-1 px-2 md:px-3 py-1 text-xs font-medium rounded transition-smooth ${
                chartType === type?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={type?.icon} size={12} />
              <span className="hidden sm:inline">{type?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Chart */}
      <div className="h-64 md:h-80 w-full mb-4 md:mb-6" aria-label="Net Worth Trend Chart">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'combined' ? (
            <AreaChart data={filteredData}>
              <defs>
                <linearGradient id="colorNetWorth" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                tickFormatter={formatShortCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="netWorth" 
                stroke="var(--color-primary)" 
                strokeWidth={3}
                fill="url(#colorNetWorth)"
                name="Net Worth"
              />
              <Line 
                type="monotone" 
                dataKey="assets" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={false}
                name="Assets"
              />
              <Line 
                type="monotone" 
                dataKey="liabilities" 
                stroke="#ef4444" 
                strokeWidth={2}
                dot={false}
                name="Liabilities"
              />
            </AreaChart>
          ) : (
            <LineChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                tickFormatter={formatShortCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey={chartType} 
                stroke={chartType === 'assets' ? '#10b981' : '#ef4444'} 
                strokeWidth={3}
                dot={{ fill: chartType === 'assets' ? '#10b981' : '#ef4444', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
      {/* Asset & Liability Breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
        <div className="p-3 md:p-4 bg-success/10 border border-success/20 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="TrendingUp" size={18} className="text-success" />
            <h4 className="text-sm md:text-base font-semibold text-foreground">Total Assets</h4>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-success mb-2">
            {formatCurrency(currentNetWorth?.totalAssets)}
          </p>
          <p className="text-xs text-muted-foreground">
            Cash, investments, property, and other valuable holdings
          </p>
        </div>

        <div className="p-3 md:p-4 bg-error/10 border border-error/20 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="TrendingDown" size={18} className="text-error" />
            <h4 className="text-sm md:text-base font-semibold text-foreground">Total Liabilities</h4>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-error mb-2">
            {formatCurrency(currentNetWorth?.totalLiabilities)}
          </p>
          <p className="text-xs text-muted-foreground">
            Credit cards, loans, mortgages, and other debts
          </p>
        </div>
      </div>
      {/* Insights */}
      <div className="mt-4 md:mt-6 p-3 md:p-4 bg-primary/10 border border-primary/20 rounded-xl">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
              Insight
            </h4>
            <p className="text-xs md:text-sm text-muted-foreground">
              {trend?.direction === 'up' 
                ? `Great progress! Your net worth has increased by ${formatCurrency(Math.abs(trend?.change))} (${trend?.percentage}%) over the selected period. Keep up the good financial habits!`
                : trend?.direction === 'down'
                ? `Your net worth has decreased by ${formatCurrency(Math.abs(trend?.change))} (${Math.abs(trend?.percentage)}%) over the selected period. Review your spending and consider adjusting your budget.`
                : 'Your net worth has remained stable. Consider strategies to increase assets or reduce liabilities for growth.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetWorthTrendChart;